import { Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { CompanyLogoAndName } from '../../../components/grid-renderers/CompanyCellRenderer';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IListItemDataModel } from '../../../data-models/list-item.data-model';
import { CompanyAuditDataModel } from '../../../schemas/CompanyAudit.schema';
import { fetchCompanyAudit } from '../../../services/queries/MaggieCompanyQueries';
import { FMT } from '../../../util/formatter-service';
import { AuditContainerCss, AuditItem } from '../../../components/AuditDetails';

interface IListAuditDetailsProps {
  company: ICompanyDataModel;
  item: IListItemDataModel;
  onClose: () => void;
}

export function ListAuditDetails({ company, item, onClose }: IListAuditDetailsProps) {
  return (
    <BasicDialog open={true} onClose={onClose} title={'Audit'} lightDismiss>
      <ListAuditContent company={company} item={item} />
    </BasicDialog>
  );
}

export function ListAuditContent({ company, item }: Pick<IListAuditDetailsProps, 'company' | 'item'>) {
  const { logoUrl, name, source } = company;
  const { addedAt } = item;

  return (
    <div css={AuditContainerCss}>
      <CompanyLogoAndName
        logoUrl={logoUrl ?? ''}
        name={name}
        avatarProps={{ size: 'medium' }}
        typographyProps={{ variant: 'body1' }}
      />
      <AuditItem label='Source' value={FMT.format('string', source)} />
      <AuditItem label='Added' value={FMT.format('date', addedAt)} />
      <AssigneeAuditDetails company={company} />
    </div>
  );
}

function AssigneeAuditDetails({ company }: { company: ICompanyDataModel }) {
  const { assignee } = company;
  const { data: auditData } = useCompanyAuditData(company.id, 'assignee');

  if (!assignee) {
    return null;
  }

  const latestEntry = auditData
    ?.toSorted((a, b) => {
      const valueA = a.updatedAt ?? a.createdAt ?? '0000-01-01';
      const valueB = b.updatedAt ?? b.createdAt ?? '0000-01-01';

      return new Date(valueB).getTime() - new Date(valueA).getTime();
    })
    .at(0);

  const assignedAtDate = latestEntry?.updatedAt ?? latestEntry?.createdAt;

  let text = `Assigned to ${FMT.format('user', assignee)}`;
  if (assignedAtDate) {
    text += ` on ${FMT.format('date', assignedAtDate)}`;
  }

  return (
    <>
      <Stack gap={2}>
        <Typography variant='body2'>{text}</Typography>
      </Stack>
    </>
  );
}

function useCompanyAuditData(companyId: number, fieldName?: keyof ICompanyDataModel) {
  return useQuery<CompanyAuditDataModel[]>({
    queryKey: ['companyAudit', { companyId, fieldName }],
    queryFn: () => fetchCompanyAudit(companyId, fieldName),
  });
}
